"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionEditCtrl = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const modal_service_1 = require("../../components/modal/modal.service");
const notification_service_1 = require("../../components/notification.toast/notification.service");
const theme_1 = require("../../components/theme");
const index_1 = require("../../i18n/index");
const services_1 = require("../../services");
const mobx_2 = require("../../state/mobx");
const page_state_1 = require("../../state/page.state");
const store_1 = require("../../store");
const upload_reader_1 = require("../../util/upload.reader");
const multi_list_chooser_style_1 = require("../multi.list.chooser//multi.list.chooser.style");
const multi_list_chooser_1 = require("../multi.list.chooser/multi.list.chooser");
const span_link_chooser_state_1 = require("../multi.list.chooser/span.link.chooser.state");
const collection_change_state_1 = require("./collection.change.state");
class CollectionEditCtrl extends mobx_2.MobxState {
    constructor(state) {
        super();
        this.isFormOpen = mobx_1.observable.map({}, { deep: false });
        this.isFieldDetailShown = mobx_1.observable.map({}, { deep: false });
        this.lastCollectionId = null;
        this.isAllExpanded = true;
        this.isInEditMode = false;
        this.isSaving = false;
        this.isMetadataShown = false;
        this.isZoomShown = true;
        this.isPhotomeasure = false;
        /** File upload */
        this.maxFileSize = 1000 * 1000 * 10; // 10 Megabyte
        this.maxImageSize = 1000 * 1000 * 4; // 4 Megabyte
        this.allowedTypes = [];
        this.isInUploadMode = false;
        this.fieldsInLoadingState = [];
        this.file = null;
        this.image = null;
        this.kind = 'CollectionEditCtrl';
        this.mapState = state;
        this.changeState = new collection_change_state_1.CollectionChangeState();
        this.reaction(_ => this.collection, this.updateEditedCollection);
        this.reaction(_ => this.file && this.file.buffer, this.processFile);
        this.reaction(_ => this.image && this.image.buffer, this.processImage);
        services_1.Services.Window.bindNavigateAway(index_1.T.DISCARD_CHANGES_CONFIRM, this.stopNavigation);
    }
    destroy() {
        super.destroy();
        if (this.changeState) {
            this.changeState.destroy();
        }
        services_1.Services.Window.unbindNavigateAway(this.stopNavigation);
        if (page_state_1.CurrentState.CollectionEdit.collectionRevision) {
            page_state_1.CurrentState.CollectionEdit.reset();
        }
    }
    get collection() {
        return page_state_1.CurrentState.CollectionEdit.collection;
    }
    get isCollectionLoading() {
        if (!page_state_1.CurrentState.inspectedCollectionRevision) {
            return false;
        }
        return !store_1.AppStore.Collection.has(page_state_1.CurrentState.inspectedCollectionRevision);
    }
    get isCollectionSelected() {
        return page_state_1.CurrentState.inspectedCollectionRevision != null;
    }
    get isEditing() {
        return this.changeState != null && (this.isInEditMode || this.isPhotomeasure);
    }
    get isValid() {
        return this.changeState != null && this.changeState.isValid && !this.isInUploadMode;
    }
    get isEditable() {
        return services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_MAP_EDIT);
    }
    get useColorCoding() {
        return services_1.Services.Token.has(core_api_1.IkeToken.PHOTOMEASURE_COLOR_CODING);
    }
    get isUploading() {
        return this.changeState != null && this.isInUploadMode;
    }
    clearOpenState() {
        this.isFormOpen.clear();
        this.isFieldDetailShown.clear();
    }
    updateEditedCollection() {
        const currentCollection = this.collection;
        if (currentCollection == null) {
            this.lastCollectionId = null;
            if (this.changeState != null) {
                this.changeState.destroy();
            }
            this.changeState = null;
            this.isInEditMode = false;
        }
        else if (this.lastCollectionId !== currentCollection.revision) {
            this.clearOpenState();
            this.lastCollectionId = currentCollection.revision;
            if (this.changeState != null) {
                this.changeState.destroy();
            }
            this.changeState = new collection_change_state_1.CollectionChangeState();
            this.isInEditMode = false;
        }
    }
    abortChanges(e) {
        e.preventDefault();
        this.mapState.checkAndDiscardChanges().then(shouldDiscard => {
            if (shouldDiscard && this.changeState != null) {
                this.discardAndDestroyEdit();
            }
        });
        return false;
    }
    discardAndDestroyEdit() {
        if (this.changeState != null) {
            const draw = page_state_1.CurrentState.CollectionEdit.currentDraw;
            if (draw != null) {
                // Reset brightness state
                draw.state.setBrightness(draw.state.defaultBrightness);
                // Reset the image visibility state
                draw.state.setImageVisibilityHidden(draw.state.defaultImageVisibilityHidden);
                // Reset the crop state
                const crop = draw.state.defaultCrop;
                draw.state.setCrop(crop.x, crop.y, crop.width, crop.height);
            }
            //discard everything
            while (page_state_1.CurrentState.CollectionEdit.isDirty) {
                page_state_1.CurrentState.CollectionEdit.reset();
            }
            // After everything has been reset, display the crosshair depending if there are any measurements
            if (draw != null) {
                draw.state.setCrosshair(!draw.drawState.hasUserMeasurements());
            }
        }
        this.changeState = new collection_change_state_1.CollectionChangeState();
        this.updateEditedCollection();
        this.isInEditMode = false;
    }
    saveChanges(e) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            e.preventDefault();
            if (!((_a = this.changeState) === null || _a === void 0 ? void 0 : _a.isValid)) {
                return;
            }
            if (!page_state_1.CurrentState.CollectionEdit.isDirty) {
                return;
            }
            if (this.isSaving) {
                return;
            }
            const originalCollection = this.collection;
            const drawState = (_b = page_state_1.CurrentState.CollectionEdit.currentDraw) === null || _b === void 0 ? void 0 : _b.state;
            if (!originalCollection) {
                return;
            }
            this.setSaving(true);
            try {
                services_1.Services.Analytics.trackTiming(core_api_1.TimingEventType.HOA, core_api_1.TimingEventAction.EXIT, originalCollection.id, new Date());
                const result = yield services_1.Services.Collection.save(originalCollection);
                this.collectionCommitted(result, originalCollection, drawState);
            }
            catch (error) {
                this.setSaving(false);
                services_1.Services.Log.Logger.error(`${error}`);
                notification_service_1.NotificationService.error({
                    key: index_1.T.COLLECTION_EDIT_SAVE_ERROR
                });
            }
        });
    }
    setSaving(save) {
        this.isSaving = save;
    }
    collectionCommitted(collection, originalCollection, drawState) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.changeState) {
                services_1.Services.Analytics.trackTiming(core_api_1.TimingEventType.PHOTOMEASURE, core_api_1.TimingEventAction.ENTER, originalCollection.id, new Date(this.changeState.startTime));
                services_1.Services.Analytics.trackTiming(core_api_1.TimingEventType.PHOTOMEASURE, core_api_1.TimingEventAction.EXIT, originalCollection.id, new Date());
                this.changeState.startTime = Date.now();
            }
            (0, mobx_1.runInAction)(() => {
                page_state_1.CurrentState.setInspectedCollection(collection.revision, page_state_1.CurrentState.inspectedCaptureId);
                // PM page will have an IkeDraw. The map page will not.
                if (page_state_1.CurrentState.CollectionEdit.currentDraw != null && drawState) {
                    page_state_1.CurrentState.CollectionEdit.currentDraw.state.setFromState(drawState);
                }
                this.setSaving(false);
            });
        });
    }
    toggleFieldDetail(fieldId) {
        if (this.isFieldDetailShown.get(fieldId)) {
            this.isFieldDetailShown.delete(fieldId);
        }
        else {
            this.isFieldDetailShown.set(fieldId, true);
        }
    }
    clearModalField(field) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield modal_service_1.ModalService.open({
                title: index_1.T.FILE_DELETE_TITLE,
                content: (0, index_1.Translate)(index_1.T.FILE_DELETE_MESSAGE, field.value),
                okName: index_1.T.FILE_DELETE,
                hasCancel: true
            });
            if (response.ok) {
                this.clearField(field);
            }
        });
    }
    clearField(field) {
        var _a;
        if (core_shared_1.CaptureProvider.isCaptureProvider(field) && page_state_1.CurrentState.inspectedCaptureId && field.getOwnedCaptureIds().includes(page_state_1.CurrentState.inspectedCaptureId)) {
            page_state_1.CurrentState.inspectedCaptureId = null;
        }
        field.Mutator.clear();
        const state = (_a = this.changeState) === null || _a === void 0 ? void 0 : _a.getState(field.id);
        if (state != null) {
            state.setValue(null);
        }
    }
    /**
     * File upload related functions
     */
    onDragOver(e, field) {
        e.preventDefault();
        const dragArea = document.getElementById(`drag-area-${field.id}`);
        if (dragArea == null) {
            return;
        }
        dragArea.style.backgroundColor = theme_1.DefaultTheme.color.accent;
    }
    onDragLeave(e, field) {
        e.preventDefault();
        const dragArea = document.getElementById(`drag-area-${field.id}`);
        if (dragArea == null) {
            return;
        }
        dragArea.style.removeProperty('backgroundColor');
    }
    onDragEnd(e, field) {
        e.preventDefault();
        const dragArea = document.getElementById(`drag-area-${field.id}`);
        if (dragArea == null) {
            return;
        }
        dragArea.style.removeProperty('backgroundColor');
    }
    selectAnchorField(e, field) {
        var _a, _b, _c;
        e.preventDefault();
        services_1.Services.Log.Logger.warn(`1. Anchor is edit by user ${(_a = services_1.Services.User.current) === null || _a === void 0 ? void 0 : _a.email.toString()} in department ${(_b = services_1.Services.Department.current) === null || _b === void 0 ? void 0 : _b.name.toString()} of organization ${(_c = services_1.Services.Department.currentOrg) === null || _c === void 0 ? void 0 : _c.name.toString()}`);
    }
    openFileDialog(e, field) {
        e.preventDefault();
        const fileInput = document.getElementById(field.id);
        if (fileInput == null) {
            throw new Error('Unable to find file input: "' + field.id + '\'');
        }
        var evt = document.createEvent('MouseEvents');
        evt.initEvent('click', true, false);
        fileInput.dispatchEvent(evt);
    }
    openImageDialog(e, field) {
        e.preventDefault();
        const imageInput = document.getElementById(field.id);
        if (imageInput == null) {
            throw new Error('Unable to find file input: "' + field.id + '\'');
        }
        var evt = document.createEvent('MouseEvents');
        evt.initEvent('click', true, false);
        imageInput.dispatchEvent(evt);
    }
    onImage(e, field) {
        const imageInput = e.target;
        if (imageInput == null) {
            throw new Error('Unable to find file input: "' + field.id + '\'');
        }
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'image:click', 'button', 1);
        if (imageInput.files) {
            this.loadImages(imageInput.files, field);
        }
    }
    onFile(e, field) {
        const fileInput = e.target;
        if (fileInput == null) {
            throw new Error('Unable to find file input: "' + field.id + '\'');
        }
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'file:click', 'button', 1);
        if (fileInput.files) {
            this.loadFiles(fileInput.files, field);
        }
    }
    update(e, field) {
        e.preventDefault();
        if (e.dataTransfer == null) {
            return;
        }
        const files = e.dataTransfer.files;
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'file:drop', 'drop', 1);
        this.loadFiles(files, field);
    }
    loadImages(images, field) {
        this.image = null;
        if (images && images[0]) {
            const image = images[0];
            if (this.maxImageSize < image.size) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'image:large', 'large', image.size);
                notification_service_1.NotificationService.error({ key: index_1.T.FILE_TOO_LARGE });
                this.unsetLoading(field.id);
                return;
            }
            const imageType = image.type.toLowerCase();
            if (this.allowedTypes.length > 0 && imageType !== '' && this.allowedTypes.indexOf(imageType) === -1) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'image:type', imageType, 1);
                notification_service_1.NotificationService.error({ key: index_1.T.FILE_NOT_SUPPORTED });
                this.unsetLoading(field.id);
                return;
            }
            this.image = new upload_reader_1.ImageUploadReader(image, field);
        }
    }
    loadFiles(files, field) {
        this.file = null;
        if (files == null) {
            return;
        }
        if (files.length > 1) {
            services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'file:count', String(files.length), 1);
            notification_service_1.NotificationService.error({ key: index_1.T.FILE_NOT_SUPPORTED });
            this.unsetLoading(field.id);
            return;
        }
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) {
                throw new Error(`undefined File recieved from FileList at index ${i}`);
            }
            if (this.maxFileSize < file.size) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'file:large', 'large', file.size);
                notification_service_1.NotificationService.error({ key: index_1.T.FILE_TOO_LARGE });
                this.unsetLoading(field.id);
                continue;
            }
            const fileType = file.type.toLowerCase();
            if (this.allowedTypes.length > 0 && fileType !== '' && this.allowedTypes.indexOf(fileType) === -1) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Upload, 'file:type', fileType, 1);
                notification_service_1.NotificationService.error({ key: index_1.T.FILE_NOT_SUPPORTED });
                this.unsetLoading(field.id);
                continue;
            }
            this.file = new upload_reader_1.FileUploadReader(file, field);
        }
    }
    processFile() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!this.file) {
                return;
            }
            this.setLoading(this.file.field.id);
            if (this.file.buffer) {
                const rest = services_1.Services.Department.rest;
                try {
                    const attachment = yield rest.Attachment.set(this.file.name, this.file.buffer, this.file.type);
                    rest.Attachment.antivirusScan(attachment.objectId);
                    let metadata = {};
                    try {
                        metadata = yield rest.Attachment.parse(attachment.objectId);
                    }
                    catch (error) {
                        services_1.Services.Log.Logger.info(`File cannot be parsed: ${error}`);
                    }
                    this.file.field.Mutator.manual({
                        filename: this.file.name,
                        attachmentId: attachment.objectId,
                        size: attachment.size,
                        metadata
                    });
                }
                catch (e) {
                    this.unsetLoading(this.file.field.id);
                    notification_service_1.NotificationService.error({ key: index_1.T.ERROR_GENERAL });
                }
            }
            this.unsetLoading(this.file.field.id);
        });
    }
    processImage() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            if ((_a = this.image) === null || _a === void 0 ? void 0 : _a.buffer) {
                this.setLoading(this.image.field.id);
                const rest = services_1.Services.Department.rest;
                let capture = null;
                try {
                    capture = yield rest.Capture.Image.image(core_shared_1.IkeId.v4(), this.image.buffer, 1, new Date());
                    rest.Capture.antivirus({ captureId: capture.objectId });
                    this.image.field.Mutator.add(capture.objectId);
                }
                catch (e) {
                    notification_service_1.NotificationService.error({ key: index_1.T.ERROR_GENERAL });
                    services_1.Services.Log.Logger.info(`Image cannot be parsed: ${e}`);
                }
                this.unsetLoading(this.image.field.id);
            }
        });
    }
    fileDownload(field) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            if (field.attachmentId) {
                this.setLoading(field.id);
                const token = `Bearer ${services_1.Services.SSO.token}`;
                const url = '/v1/file/' + field.attachmentId + '?departmentId=' + ((_a = services_1.Services.Department.current) === null || _a === void 0 ? void 0 : _a.objectId);
                const options = {
                    headers: {
                        Authorization: token
                    }
                };
                // @todo this needs to be refactored to use the rest classes
                fetch(url, options)
                    .then(res => {
                    if (!res.ok) {
                        services_1.Services.Log.Logger.error('File possibly infected');
                        notification_service_1.NotificationService.error({
                            key: index_1.T.ERROR_DOWNLOAD_FAIL
                        });
                        return null;
                    }
                    return res.blob();
                })
                    .then(blob => {
                    var _a;
                    if (blob != null) {
                        var file = window.URL.createObjectURL(blob);
                        var link = services_1.Services.Window.document.createElement('a');
                        link.download = (_a = field.value) !== null && _a !== void 0 ? _a : '';
                        link.href = file;
                        services_1.Services.Window.document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }
                    this.unsetLoading(field.id);
                })
                    .catch((e) => {
                    services_1.Services.Log.Logger.error(e, 'File download error');
                    notification_service_1.NotificationService.error({
                        key: index_1.T.ERROR_GENERAL
                    });
                    this.unsetLoading(field.id);
                });
            }
            return false;
        });
    }
    setLoading(fieldId) {
        this.isInUploadMode = true;
        this.fieldsInLoadingState.push(fieldId);
    }
    unsetLoading(fieldId) {
        this.isInUploadMode = false;
        this.fieldsInLoadingState.splice(this.fieldsInLoadingState.indexOf(fieldId), 1);
    }
    /**
     * File upload related functions end
     */
    fillAddressDisabled(field) {
        if (field.value) {
            return true;
        }
        if (this.location() == null) {
            return true;
        }
        if (!this.isEditing || this.isSaving) {
            return true;
        }
        return false;
    }
    geocodingButtonTitle(field) {
        if (field.value) {
            return 'Already has value';
        }
        if (this.location() == null) {
            return 'There is no location';
        }
        return 'Get address';
    }
    location() {
        const revision = page_state_1.CurrentState.inspectedCollectionRevision;
        if (!revision) {
            return null;
        }
        const collectionSummary = store_1.AppStore.CollectionSummary.get(revision);
        const location = collectionSummary.location;
        if (!(location && location.coordinates)) {
            return null;
        }
        return location;
    }
    fillAddress(field) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const location = this.location();
            if (location == null) {
                return;
            }
            if (!this.changeState) {
                return;
            }
            const addressFields = yield services_1.Services.Geocoder.getAddress(location);
            const address = addressFields.address;
            const fieldState = this.changeState.getState(field.id);
            fieldState.setValue(address);
            if (address === (0, index_1.Translate)(index_1.T.ERROR_GENERAL)) {
                fieldState.setError(address);
            }
            const metadata = field.collection.schema.metadata;
            const city = addressFields.city;
            const state = addressFields.state;
            if (city && ((_a = metadata.options.address_lookup) === null || _a === void 0 ? void 0 : _a.city)) {
                const cityFieldId = metadata.options.address_lookup.city.field;
                const cityFieldState = this.changeState.getState(core_shared_1.CollectionModelField.getStateId(field.parent, cityFieldId));
                if (cityFieldState) {
                    cityFieldState.setValue(city);
                }
            }
            if (state && ((_b = metadata.options.address_lookup) === null || _b === void 0 ? void 0 : _b.state)) {
                const stateFieldId = metadata.options.address_lookup.state.field;
                const stateFieldState = this.changeState.getState(core_shared_1.CollectionModelField.getStateId(field.parent, stateFieldId));
                if (stateFieldState) {
                    stateFieldState.setValue(state);
                }
            }
        });
    }
    clearError(field) {
        if (!this.changeState) {
            return;
        }
        const fieldState = this.changeState.getState(field.id);
        fieldState.setValue('');
        fieldState.setError('');
    }
    clearErrorDisabled(field) {
        if (!this.isEditing || this.isSaving) {
            return true;
        }
        if (!this.changeState) {
            return true;
        }
        const state = this.changeState.getState(field.id);
        if (state.hasError() === true) {
            return false;
        }
        return true;
    }
    isExpanded(instanceId) {
        var _a;
        if (this.isAllExpanded) {
            return this.isFormOpen.get(instanceId) !== false;
        }
        return (_a = this.isFormOpen.get(instanceId)) !== null && _a !== void 0 ? _a : false;
    }
    toggleExpanded(e, instanceId) {
        e.preventDefault();
        let prev = this.isFormOpen.get(instanceId);
        if (prev == null) {
            prev = this.isAllExpanded;
        }
        this.isFormOpen.set(instanceId, !prev);
        return false;
    }
    setZoomWindow(show) {
        this.isZoomShown = show;
    }
    zoomToCollection() {
        if (page_state_1.CurrentState.inspectedCollectionRevision) {
            this.mapState.zoomToCollection(page_state_1.CurrentState.inspectedCollectionRevision);
        }
    }
    toggleMetaInfo() {
        this.isMetadataShown = !this.isMetadataShown;
        this.isZoomShown = false;
    }
    toggleZoomWindow() {
        var _a;
        this.isZoomShown = !this.isZoomShown;
        this.isMetadataShown = false;
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Collection, `zoom:${this.isZoomShown ? 'show' : 'hide'}`, (_a = this.collection) === null || _a === void 0 ? void 0 : _a.revision);
    }
    toggleExpandAll() {
        var _a;
        this.isAllExpanded = !this.isAllExpanded;
        this.isFormOpen.clear();
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Collection, `form:${this.isAllExpanded ? 'expand' : 'collapse'}`, (_a = this.collection) === null || _a === void 0 ? void 0 : _a.revision);
    }
    enterEditMode() {
        if (!page_state_1.CurrentState.inspectedCollectionRevision) {
            return;
        }
        const collection = store_1.AppStore.Collection.get(page_state_1.CurrentState.inspectedCollectionRevision);
        if (collection != null) {
            services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'Collection:edit', collection.uuid, 1);
        }
        this.isInEditMode = true;
    }
    exitEditMode() {
        if (page_state_1.CurrentState.CollectionEdit.isDirty) {
            throw new Error('Must discard changes before exiting collection edit mode');
        }
        this.isInEditMode = false;
    }
    stopNavigation() {
        return page_state_1.CurrentState.CollectionEdit.isDirty;
    }
    beginSpanLink(c) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            const collection = c.collection;
            const state = new span_link_chooser_state_1.SpanLinkChooserState(collection);
            const result = yield modal_service_1.ModalService.open({
                title: multi_list_chooser_1.MultiListChooserTitle.view(state),
                content: () => multi_list_chooser_1.MultiListChooserContent.view(state),
                className: multi_list_chooser_style_1.CSS.modal,
                hasCancel: true
            });
            if (!result.ok) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:cancel', (_a = this.collection) === null || _a === void 0 ? void 0 : _a.revision);
                return;
            }
            if (state.value == null) {
                state.selectDefaultValue();
            }
            const spanLinkRecord = state.getSelectedSpanLinkRecord();
            if (spanLinkRecord == null) {
                return;
            }
            (0, mobx_1.runInAction)(() => {
                var _a;
                try {
                    const newSubformInstance = services_1.Services.SubFormLink.copySubForm(collection, spanLinkRecord);
                    notification_service_1.NotificationService.success({
                        key: index_1.T.SPAN_LINK_SUCCESS,
                        attrs: {
                            toSpan: newSubformInstance.title,
                            fromCollection: spanLinkRecord.collection.title
                        }
                    });
                    services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link', (_a = this.collection) === null || _a === void 0 ? void 0 : _a.revision);
                }
                catch (e) {
                    services_1.Services.Log.Logger.error(`Span link failed: ${e}`);
                    notification_service_1.NotificationService.error({
                        key: index_1.T.ERROR_GENERAL
                    });
                }
            });
        });
    }
    get matches() {
        if (this.collection == null || !services_1.Services.Measurement.isFormSupported(this.collection.schema)) {
            return [];
        }
        return services_1.Services.Measurement.getFieldJoins(this.collection);
    }
    getJoin(field) {
        for (const match of this.matches) {
            const join = match.getJoin(field.parent.instanceId, field.schema.id);
            if (join != null) {
                return join;
            }
        }
        return null;
    }
    getOppositeJoin(field) {
        for (const match of this.matches) {
            const join = match.getOppositeJoin(field.parent.instanceId, field.schema.id);
            if (join != null) {
                return join;
            }
        }
        return null;
    }
    chooseLinkingCollection(field) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            page_state_1.CurrentState.CollectionLink.beginLinking(field.id);
            return new Promise(resolve => {
                const disposer = (0, mobx_1.reaction)(() => page_state_1.CurrentState.CollectionLink.isLinking, () => {
                    disposer();
                    const revision = page_state_1.CurrentState.CollectionLink.collectionRevision;
                    if (revision == null) {
                        resolve(null);
                    }
                    const collectionSummary = store_1.AppStore.CollectionSummary.get(revision);
                    resolve(collectionSummary);
                });
            });
        });
    }
    canInspectCollection(summary) {
        if (summary == null) {
            return false;
        }
        const id = summary.tags[0];
        if (!id) {
            return false;
        }
        const tag = store_1.AppStore.Tag.get(id);
        if (!tag) {
            return false;
        }
        return services_1.Services.Search.includesTag(tag);
    }
    inspectCollection(revision) {
        this.mapState.zoomToCollection(revision);
    }
}
exports.CollectionEditCtrl = CollectionEditCtrl;
tslib_1.__decorate([
    mobx_1.observable.ref
], CollectionEditCtrl.prototype, "changeState", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isAllExpanded", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isInEditMode", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isSaving", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isMetadataShown", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isZoomShown", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isPhotomeasure", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "isInUploadMode", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionEditCtrl.prototype, "fieldsInLoadingState", void 0);
tslib_1.__decorate([
    mobx_1.observable.shallow
], CollectionEditCtrl.prototype, "file", void 0);
tslib_1.__decorate([
    mobx_1.observable.shallow
], CollectionEditCtrl.prototype, "image", void 0);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "clearOpenState", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "updateEditedCollection", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "abortChanges", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "discardAndDestroyEdit", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "saveChanges", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "setSaving", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "toggleFieldDetail", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "clearModalField", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "clearField", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "onDragOver", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "onDragLeave", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "onDragEnd", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "selectAnchorField", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "openFileDialog", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "openImageDialog", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "onImage", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "onFile", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "update", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "processFile", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "processImage", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "fileDownload", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "fillAddress", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "clearError", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "toggleExpanded", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "setZoomWindow", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "zoomToCollection", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "toggleMetaInfo", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "toggleZoomWindow", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "toggleExpandAll", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "enterEditMode", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionEditCtrl.prototype, "exitEditMode", null);
tslib_1.__decorate([
    mobx_1.action.bound
], CollectionEditCtrl.prototype, "stopNavigation", null);
tslib_1.__decorate([
    mobx_1.computed
], CollectionEditCtrl.prototype, "matches", null);
//# sourceMappingURL=collection.view.ctrl.js.map